import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, ImagePlus, Plus, Trash2, Lock, Globe, MailWarning } from "lucide-react";
import { api, API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const input = "w-full text-sm border border-zinc-200 rounded-xl px-3 py-2.5 outline-none focus:border-[#c8102e] bg-white";

function Choice({ value, current, onChange, icon: Icon, title, text, testid }) {
  return (
    <button type="button" onClick={() => onChange(value)} data-testid={testid} data-active={current === value}
      className="flex-1 text-left p-3 rounded-xl border border-zinc-200 data-[active=true]:border-[#c8102e] data-[active=true]:bg-[#c8102e]/5 transition-colors">
      <div className="text-sm font-semibold inline-flex items-center gap-1"><Icon size={14} /> {title}</div>
      <div className="text-[11px] text-zinc-500 mt-0.5 leading-snug">{text}</div>
    </button>
  );
}

export default function GroupCreate() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [cats, setCats] = useState([]);
  const [cities, setCities] = useState([]);
  const [f, setF] = useState({ name: "", description: "", category: "", city: "", privacy: "public", join_policy: "open", cover_url: "" });
  const [rules, setRules] = useState([]);
  const [busy, setBusy] = useState(false);
  const [enabled, setEnabled] = useState(null);
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  useEffect(() => {
    api.get("/communities/config").then(r => setEnabled(r.data.user_groups_enabled || user?.role === "admin")).catch(() => setEnabled(false));
    api.get("/groups/categories").then(r => setCats(r.data)).catch(() => {});
    api.get("/cities").then(r => setCities(r.data.map(c => c.name))).catch(() => {});
  }, [user]);
  if (enabled === false) return (
    <div className="page pt-10 text-center" data-testid="group-create-disabled">
      <p className="text-sm font-semibold">A criação de grupos por membros está desativada.</p>
      <p className="text-xs text-zinc-500 mt-1 mb-4">As comunidades são criadas pela equipa ANGOLAUK+. Podes propor uma nova na página de Comunidades.</p>
      <Link to="/comunidade" className="px-4 py-2 rounded-xl bg-black text-white text-sm font-semibold">Ir para Comunidades</Link>
    </div>
  );

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); set("cover_url", `${API}/files/${data.file_id}`); toast.success("Capa carregada"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Erro no upload"); }
    e.target.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (f.name.trim().length < 3) return toast.error("Nome: mínimo 3 caracteres");
    if (f.description.trim().length < 10) return toast.error("Descrição: mínimo 10 caracteres");
    if (!f.category) return toast.error("Escolhe uma categoria");
    setBusy(true);
    try {
      const { data } = await api.post("/groups", { ...f, name: f.name.trim(), description: f.description.trim(), city: f.city || null, cover_url: f.cover_url || null,
        rules: rules.filter(r => r.title.trim()).map(r => ({ title: r.title.trim(), text: (r.text || "").trim() })) });
      toast.success("Grupo criado!");
      nav(`/grupos/${data.slug}`);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(formatApiError(d) || "Erro ao criar grupo");
    } finally { setBusy(false); }
  };

  const unverified = user && user.email_verified === false;

  return (
    <div className="page fade-up" data-testid="group-create">
      <Link to="/comunidade" className="inline-flex items-center gap-1 text-xs text-zinc-500 mt-3 mb-2" data-testid="group-create-back"><ChevronLeft size={14} /> Comunidades</Link>
      <h1 className="font-display text-2xl font-extrabold mb-1">Criar grupo</h1>
      <p className="text-sm text-zinc-500 mb-4">Um espaço criado por ti para a comunidade. Grupos não têm o selo Oficial e seguem as regras da ANGOLAUK+.</p>

      {unverified && (
        <div className="mb-4 p-3 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-900 flex gap-2" data-testid="group-create-unverified">
          <MailWarning size={16} className="shrink-0 mt-0.5" />
          <div>Para criar um grupo precisas de confirmar o teu e-mail. <Link to="/perfil" className="underline font-semibold">Confirmar agora</Link></div>
        </div>
      )}

      <form onSubmit={submit} className="space-y-4">
        <label className="block"><span className="text-xs font-semibold">Capa (opcional)</span>
          <div className="mt-1 aspect-[3/1] rounded-xl bg-zinc-100 overflow-hidden relative">
            {f.cover_url && <img src={f.cover_url} alt="" className="w-full h-full object-cover" data-testid="group-create-cover-preview" />}
            <label className="absolute bottom-2 right-2 bg-white/90 rounded-full px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1 cursor-pointer"><ImagePlus size={12} /> {f.cover_url ? "Trocar" : "Carregar"}<input type="file" accept="image/*" className="hidden" onChange={upload} data-testid="group-create-cover-input" /></label>
          </div></label>
        <label className="block"><span className="text-xs font-semibold">Nome do grupo</span><input value={f.name} onChange={e => set("name", e.target.value)} maxLength={80} className={`${input} mt-1`} placeholder="Ex: Mães Angolanas em Londres" data-testid="group-create-name" /></label>
        <label className="block"><span className="text-xs font-semibold">Descrição</span><textarea value={f.description} onChange={e => set("description", e.target.value)} maxLength={1000} rows={4} className={`${input} mt-1 resize-none`} placeholder="Para quem é este grupo e o que se partilha aqui?" data-testid="group-create-description" /><span className="text-[10px] text-zinc-400">{f.description.length}/1000</span></label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block"><span className="text-xs font-semibold">Categoria</span>
            <select value={f.category} onChange={e => set("category", e.target.value)} className={`${input} mt-1`} data-testid="group-create-category">
              <option value="">Escolher…</option>{cats.map(c => <option key={c} value={c}>{c}</option>)}
            </select></label>
          <label className="block"><span className="text-xs font-semibold">Localização (opcional)</span>
            <select value={f.city} onChange={e => set("city", e.target.value)} className={`${input} mt-1`} data-testid="group-create-city">
              <option value="">Todo o Reino Unido</option>{cities.map(c => <option key={c} value={c}>{c}</option>)}
            </select></label>
        </div>
        <div><span className="text-xs font-semibold">Visibilidade</span>
          <div className="flex gap-2 mt-1">
            <Choice value="public" current={f.privacy} onChange={v => set("privacy", v)} icon={Globe} title="Público" text="Qualquer pessoa vê as publicações e os membros" testid="group-create-privacy-public" />
            <Choice value="private" current={f.privacy} onChange={v => set("privacy", v)} icon={Lock} title="Privado" text="Aparece na lista, mas o conteúdo é só para membros" testid="group-create-privacy-private" />
          </div></div>
        <div><span className="text-xs font-semibold">Entrada</span>
          <div className="flex gap-2 mt-1">
            <Choice value="open" current={f.join_policy} onChange={v => set("join_policy", v)} icon={Globe} title="Livre" text="Quem quiser entra de imediato" testid="group-create-join-open" />
            <Choice value="approval" current={f.join_policy} onChange={v => set("join_policy", v)} icon={Lock} title="Com aprovação" text="Tu ou os moderadores aprovam cada pedido" testid="group-create-join-approval" />
          </div></div>
        <div>
          <div className="flex items-center justify-between"><span className="text-xs font-semibold">Regras do grupo ({rules.length}/10)</span>
            <button type="button" onClick={() => rules.length < 10 && setRules(rs => [...rs, { title: "", text: "" }])} data-testid="group-create-add-rule" className="text-xs inline-flex items-center gap-1 font-semibold"><Plus size={12} /> Adicionar</button></div>
          <div className="space-y-2 mt-1">{rules.map((r, i) => (
            <div key={i} className="border border-zinc-200 rounded-xl p-2 space-y-1" data-testid={`group-create-rule-${i}`}>
              <div className="flex gap-1"><input value={r.title} onChange={e => setRules(rs => rs.map((x, j) => j === i ? { ...x, title: e.target.value } : x))} maxLength={80} placeholder={`Regra ${i + 1} — título`} className={input} data-testid={`group-create-rule-title-${i}`} />
                <button type="button" onClick={() => setRules(rs => rs.filter((_, j) => j !== i))} aria-label="Remover regra" className="px-2 text-zinc-500"><Trash2 size={14} /></button></div>
              <textarea value={r.text} onChange={e => setRules(rs => rs.map((x, j) => j === i ? { ...x, text: e.target.value } : x))} maxLength={500} rows={2} placeholder="Descrição (opcional)" className={`${input} resize-none`} />
            </div>))}</div>
          {rules.length === 0 && <p className="text-[11px] text-zinc-400 mt-1">Sem regras próprias aplicam-se as regras gerais da ANGOLAUK+.</p>}
        </div>
        <p className="text-[11px] text-zinc-500">Máximo de 3 grupos por utilizador. Nomes que sugiram ser oficiais ou da equipa ANGOLAUK+ não são permitidos. A equipa ANGOLAUK+ pode suspender grupos que violem os termos.</p>
        <button type="submit" disabled={busy || unverified} data-testid="group-create-submit" className="w-full py-3 mb-6 rounded-xl bg-[#c8102e] text-white text-sm font-semibold disabled:opacity-40">{busy ? "A criar…" : "Criar grupo"}</button>
      </form>
    </div>
  );
}
